import { QuestionnaireAndAnswers, QuestionnaireType } from '../../../../types/Questionnaire';
import { Patient } from '../../../../types/Patient';

export const getUrlPart = (questionnaireType: QuestionnaireType) => {
  switch (questionnaireType) {
    case QuestionnaireType.PreOpChart:
      return 'preop';
    case QuestionnaireType.OrChart:
      return 'or';
    case QuestionnaireType.PacuChart:
      return 'pacu';
    case QuestionnaireType.PhysicianChart:
      return 'physician';
    case QuestionnaireType.AnesthesiologistPreOpChart:
      return 'anesthesiologist-preop';
    case QuestionnaireType.AnesthesiologistOrChart:
      return 'anesthesiologist-or';
    case QuestionnaireType.AnesthesiologistPacuChart:
      return 'anesthesiologist-pacu';
    default:
      return 'preop';
  }
};

export const getChartingName = (questionnaireType: QuestionnaireType) => {
  switch (questionnaireType) {
    case QuestionnaireType.PreOpChart:
      return 'PreOp';
    case QuestionnaireType.OrChart:
      return 'OR';
    case QuestionnaireType.PacuChart:
      return 'PACU';
    case QuestionnaireType.PhysicianChart:
      return 'Physician';
    case QuestionnaireType.AnesthesiologistPreOpChart:
      return 'Anesthesiologist';
    case QuestionnaireType.AnesthesiologistOrChart:
      return 'Anesthesiologist';
    case QuestionnaireType.AnesthesiologistPacuChart:
      return 'Anesthesiologist';
    default:
      return 'PreOp';
  }
};

/** Prefer exact version match; else highest version <= snapshot, one tile per questionnaire name. */
export const resolveChartsForProcedure = (
  charts: QuestionnaireAndAnswers[] = [],
  chartTemplateVersion: number = 1,
): QuestionnaireAndAnswers[] => {
  const byName = new Map<string, QuestionnaireAndAnswers[]>();

  charts.forEach(chart => {
    const name = chart?.questionnaire?.name;
    if (!name) return;
    const version = chart?.questionnaire?.version ?? 1;
    if (version > chartTemplateVersion) return;
    const existing = byName.get(name) || [];
    existing.push(chart);
    byName.set(name, existing);
  });

  return Array.from(byName.values())
    .map(rows => {
      const exact = rows.find(row => (row.questionnaire?.version ?? 1) === chartTemplateVersion);
      if (exact) return exact;
      return rows.reduce((best, row) =>
        (row.questionnaire?.version ?? 1) > (best.questionnaire?.version ?? 1) ? row : best,
      );
    })
    .sort(
      (a, b) =>
        (a.questionnaire?.order ?? Number.MAX_SAFE_INTEGER) -
        (b.questionnaire?.order ?? Number.MAX_SAFE_INTEGER),
    );
};

export const getAllChartingData = (patient?: Patient) => {
  const version = patient?.procedure?.chartTemplateVersion ?? 1;
  return [
    ...resolveChartsForProcedure(patient?.procedure?.preOpCharts, version),
    ...resolveChartsForProcedure(patient?.procedure?.orCharts, version),
    ...resolveChartsForProcedure(patient?.procedure?.pacuCharts, version),
    ...resolveChartsForProcedure(patient?.procedure?.physicianCharts, version),
    ...resolveChartsForProcedure(patient?.procedure?.anesthesiologistPreOpCharts, version),
    ...resolveChartsForProcedure(patient?.procedure?.anesthesiologistOrCharts, version),
    ...resolveChartsForProcedure(patient?.procedure?.anesthesiologistPacuCharts, version),
  ];
};

export const getChartingData = (questionnaireType: QuestionnaireType, patient?: Patient) => {
  const version = patient?.procedure?.chartTemplateVersion ?? 1;
  switch (questionnaireType) {
    case QuestionnaireType.PreOpChart:
      return resolveChartsForProcedure(patient?.procedure?.preOpCharts, version);
    case QuestionnaireType.OrChart:
      return resolveChartsForProcedure(patient?.procedure?.orCharts, version);
    case QuestionnaireType.PacuChart:
      return resolveChartsForProcedure(patient?.procedure?.pacuCharts, version);
    case QuestionnaireType.PhysicianChart:
      return resolveChartsForProcedure(patient?.procedure?.physicianCharts, version);
    case QuestionnaireType.AnesthesiologistPreOpChart:
      return resolveChartsForProcedure(patient?.procedure?.anesthesiologistPreOpCharts, version);
    case QuestionnaireType.AnesthesiologistOrChart:
      return resolveChartsForProcedure(patient?.procedure?.anesthesiologistOrCharts, version);
    case QuestionnaireType.AnesthesiologistPacuChart:
      return resolveChartsForProcedure(patient?.procedure?.anesthesiologistPacuCharts, version);
    default:
      return [];
  }
};

export const getChartingDataFrom = (questionnaireType: QuestionnaireType, data?: any) => {
  const version = data?.chartTemplateVersion ?? 1;
  switch (questionnaireType) {
    case QuestionnaireType.PreOpChart:
      return resolveChartsForProcedure(data?.preOpCharts, version);
    case QuestionnaireType.OrChart:
      return resolveChartsForProcedure(data?.orCharts, version);
    case QuestionnaireType.PacuChart:
      return resolveChartsForProcedure(data?.pacuCharts, version);
    case QuestionnaireType.PhysicianChart:
      return resolveChartsForProcedure(data?.physicianCharts, version);
    case QuestionnaireType.AnesthesiologistPreOpChart:
      return resolveChartsForProcedure(data?.anesthesiologistPreOpCharts, version);
    case QuestionnaireType.AnesthesiologistOrChart:
      return resolveChartsForProcedure(data?.anesthesiologistOrCharts, version);
    case QuestionnaireType.AnesthesiologistPacuChart:
      return resolveChartsForProcedure(data?.anesthesiologistPacuCharts, version);
    default:
      return [];
  }
};

export const getChartingDatQueryName = (questionnaireType: QuestionnaireType) => {
  switch (questionnaireType) {
    case QuestionnaireType.PreOpChart:
      return 'preOpCharts';
    case QuestionnaireType.OrChart:
      return 'orCharts';
    case QuestionnaireType.PacuChart:
      return 'pacuCharts';
    case QuestionnaireType.PhysicianChart:
      return 'physicianCharts';
    case QuestionnaireType.AnesthesiologistPreOpChart:
      return 'anesthesiologistPreOpCharts';
    case QuestionnaireType.AnesthesiologistOrChart:
      return 'anesthesiologistOrCharts';
    case QuestionnaireType.AnesthesiologistPacuChart:
      return 'anesthesiologistPacuCharts';
    default:
      return 'preOpCharts';
  }
};
